/**
 * Reading /api/auth/check-session without turning a hiccup into a sign-out.
 *
 * The probe has three possible answers, and only one of them means the user is
 * signed out. Collapsing "the server could not answer" into "you are signed out"
 * is what logged people straight back out after a magic-link sign-in: the probe
 * ran out of rate-limit budget, answered 429, and the dashboard shell redirected
 * to /login while the session cookie was still valid.
 *
 * Nothing here grants access. Every dashboard API authenticates on the server;
 * this only decides what the client shows and whether it redirects.
 */

export type SessionProbeOutcome =
  | { kind: 'authenticated'; email: string; isAdmin: boolean }
  /** The server answered, and the answer is: no session. Safe to redirect. */
  | { kind: 'unauthenticated' }
  /** The server did not give a usable answer. Retry; never redirect. */
  | { kind: 'indeterminate'; reason: string };

/**
 * Backoff for a probe the server could not answer (429/5xx/offline):
 * ~0.8s, 1.6s, 3.2s, 6.4s. Long enough to ride out a rate-limit window edge or
 * a redeploy without leaving the user staring at a spinner.
 */
export const SESSION_PROBE_MAX_RETRIES = 4;
const SESSION_PROBE_BASE_DELAY_MS = 800;

export function sessionProbeRetryDelay(attempt: number): number {
  return SESSION_PROBE_BASE_DELAY_MS * 2 ** attempt;
}

/**
 * How long one full retry chain takes (~12s): the sum of every backoff step.
 *
 * A focus event may not start a fresh chain until this has passed since the
 * last one started. Returning to the tab is what people do while waiting, and
 * each return used to add another five requests to the budget the endpoint was
 * already rate-limiting them out of. Deriving the floor from the chain itself
 * keeps a returning user from ever waiting longer than sitting still would.
 */
export const SESSION_PROBE_COOLDOWN_MS = Array.from(
  { length: SESSION_PROBE_MAX_RETRIES },
  (_, attempt) => sessionProbeRetryDelay(attempt)
).reduce((total, delay) => total + delay, 0);

/**
 * May a focus event start a session probe? No while one is running (join that
 * chain instead of adding to it), and no again until the cooldown has passed.
 */
export function canStartSessionProbe(state: {
  inFlight: boolean;
  lastStartedAt: number;
  now: number;
}): boolean {
  if (state.inFlight) return false;
  return state.now - state.lastStartedAt >= SESSION_PROBE_COOLDOWN_MS;
}

/**
 * What a gate should do about one probe answer, given how many it has already
 * had. One definition for both gates, so the admin page and the dashboard shell
 * cannot drift apart about when to stop retrying or what to do then.
 */
export type SessionGateDecision =
  | { kind: 'authenticated'; email: string; isAdmin: boolean }
  | { kind: 'unauthenticated' }
  /** Try again in `delayMs`. */
  | { kind: 'retry'; delayMs: number }
  /**
   * Out of retries with no usable answer. NOT a sign-out: the session may well
   * be valid, so say so and offer a retry rather than redirecting or rendering
   * nothing. Leaving this outcome unnamed is what left the dashboard on a
   * spinner that could not resolve and the admin page blank.
   */
  | { kind: 'unverified'; reason: string };

/**
 * @param attempt Zero-based index of the attempt that produced `outcome`.
 */
export function decideSessionGate(
  outcome: SessionProbeOutcome,
  attempt: number
): SessionGateDecision {
  if (outcome.kind !== 'indeterminate') return outcome;

  if (attempt < SESSION_PROBE_MAX_RETRIES) {
    return { kind: 'retry', delayMs: sessionProbeRetryDelay(attempt) };
  }

  return { kind: 'unverified', reason: outcome.reason };
}

/** The shape we care about from a check-session response body. */
interface SessionProbeBody {
  authenticated?: unknown;
  email?: unknown;
  isAdmin?: unknown;
}

/**
 * @param response  Status of the fetch, or null if the request never completed.
 * @param body      Parsed JSON body, or null if it was missing/unparseable.
 */
export function classifySessionProbe(
  response: { ok: boolean; status: number } | null,
  body: unknown
): SessionProbeOutcome {
  if (!response) {
    return { kind: 'indeterminate', reason: 'network-error' };
  }

  // Only the server saying "no session" signs a user out. 429 (out of rate-limit
  // budget) and 5xx are the server declining to answer, not an answer.
  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthenticated' };
  }

  if (response.status === 429) {
    return { kind: 'indeterminate', reason: 'rate-limited' };
  }

  if (!response.ok) {
    return { kind: 'indeterminate', reason: `http-${response.status}` };
  }

  if (typeof body !== 'object' || body === null) {
    return { kind: 'indeterminate', reason: 'unreadable-body' };
  }

  const parsed = body as SessionProbeBody;

  if (parsed.authenticated === false) {
    return { kind: 'unauthenticated' };
  }

  if (parsed.authenticated === true && typeof parsed.email === 'string' && parsed.email) {
    return {
      kind: 'authenticated',
      email: parsed.email,
      isAdmin: parsed.isAdmin === true,
    };
  }

  // Authenticated with no email, or no `authenticated` field at all: the body is
  // not something we can act on, and guessing either way is worse than retrying.
  return { kind: 'indeterminate', reason: 'unexpected-body' };
}
